import styled from '@emotion/styled';
import { Button, Modal, Typography } from '@material-ui/core';
import { ClassAttributes, createElement, FC, useContext, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';

import YoutubeChannel from '../../../entity/YoutubeChannel';
import { context as DependencyContext } from '../../context/dependency';
import { context as StateContext } from '../../context/state';

type Props = ClassAttributes<HTMLElement> & {
  className?: string;
};

const YoutubeChannelConfiguration: FC<Props> = ({ className }) => {
  const state = useContext(StateContext);
  const dependency = useContext(DependencyContext);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDeleteChannel, setSelectedDeleteChannel] = useState<YoutubeChannel | null>(null);
  const youtubeChannels = state.youtubeChannels;

  const fetchYoutubeChannels = async () => dependency.sendCreativeToYoutubeUsecase.fetchChannels();
  const onIntegrateYoutubeChannel = async () => dependency.sendCreativeToYoutubeUsecase.addConnectedChannel();
  const onRevokeYoutubeChannel = (channel: YoutubeChannel) => {
    dependency.sendCreativeToYoutubeUsecase.disposeYoutubeChannel(channel);
  };
  const onClickDeleteButton = (_: any, selected: YoutubeChannel) => {
    setOpenModal(true);
    setSelectedDeleteChannel(selected);
  };
  const onClickDeleteSubmitButton = (_: any) => {
    if (selectedDeleteChannel !== null) {
      onRevokeYoutubeChannel(selectedDeleteChannel);
    }
    setSelectedDeleteChannel(null);
    setOpenModal(false);
  };
  const onCancelDelete = () => {
    setSelectedDeleteChannel(null);
    setOpenModal(false);
  };

  return (
    <Root className={className}>
      <LinkedAccountList>
        {youtubeChannels.map((channel) => (
          <LinkedAccountItem key={channel.channelId}>
            <LinkedAccountItemBody>
              <LinkedAccountThumbnailImage src={channel.thumbnailUrl} />
              {channel.name}
            </LinkedAccountItemBody>
            <LinkedAccountItemDelete
              onClick={(e) => {
                onClickDeleteButton(e, channel);
              }}
            >
              ×
            </LinkedAccountItemDelete>
          </LinkedAccountItem>
        ))}
      </LinkedAccountList>
      <StyledButton
        variant="contained"
        color="primary"
        onClick={async () => {
          await onIntegrateYoutubeChannel();
          await fetchYoutubeChannels();
        }}
      >
        <FormattedMessage id="configuration_page.external_account_connect_settings.button" />
      </StyledButton>

      <YoutubeServiceLinkBox>
        <FormattedHTMLMessage
          id="configuration_page.youtube_oauth_unlink"
          values={{
            url: 'https://myaccount.google.com/permissions',
          }}
        />
      </YoutubeServiceLinkBox>
      <YoutubeServiceLinkBox>
        <FormattedHTMLMessage
          id="configuration_page.youtube_terms_of_services"
          values={{
            url: 'https://www.youtube.com/t/terms',
          }}
        />
      </YoutubeServiceLinkBox>
      <YoutubeServiceLinkBox>
        <FormattedHTMLMessage
          id="configuration_page.google_privacy_policy"
          values={{
            url: 'https://policies.google.com/privacy',
          }}
        />
      </YoutubeServiceLinkBox>
      <Modal open={openModal} onClose={onCancelDelete}>
        <ModalStyle>
          <ModalTitle>
            <Typography>
              <FormattedMessage id="configuration_page.youtube_channel_revoke_modal_label" />
            </Typography>
          </ModalTitle>
          <Button
            onClick={(e) => {
              onClickDeleteSubmitButton(e);
            }}
            fullWidth={true}
          >
            <FormattedMessage id="configuration_page.external_account_revoke_modal_button" />
          </Button>
        </ModalStyle>
      </Modal>
    </Root>
  );
};

const Root = styled('div')();

const LinkedAccountList = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
});

const LinkedAccountItem = styled('div')({
  display: 'flex',
  position: 'relative',
});

const LinkedAccountItemBody = styled('div')({
  display: 'flex',
  alignItems: 'center',
  fontSize: '18px',
  color: '#444',
  backgroundColor: 'white',
  border: '1px solid #ccc',
  padding: '8px 14px',
  marginRight: '8px',
  marginBottom: '8px',
  minHeight: '24px',
});

const LinkedAccountItemDelete = styled('div')({
  display: 'flex',
  position: 'absolute',
  top: '-8px',
  right: '0px',
  fontSize: '12px',
  borderRadius: '999px',
  width: '20px',
  height: '20px',
  backgroundColor: 'gray',
  color: '#ffffff',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  opacity: 0.9,
  ':hover': {
    opacity: 1,
  },
});

const LinkedAccountThumbnailImage = styled('img')({
  width: '18px',
  height: '18px',
  marginRight: '5px',
});

const ModalStyle = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '450',
  background: 'white',
  outline: 'none',
  boxShadow: '0 3px 7px rgba(0, 0, 0, 0.3)',
  borderRadius: '10px',
  border: '1px solid rgba(0, 0, 0, 0.3)',
  padding: '16px',
});

const ModalTitle = styled('div')({
  fontSize: '18px',
  color: '#222',
  margin: '32px 0px',
});

const YoutubeServiceLinkBox = styled('div')({
  margin: '10px 0px 0px 0px',
});

const StyledButton = styled(Button)();

export default YoutubeChannelConfiguration;
